import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AppShell, PageHeader } from '@/components/AppShell';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  PanelLeftOpen,
  Plus,
  Pencil,
  Trash2,
  Tag,
  Loader2,
} from 'lucide-react';
import {
  listBrands,
  createBrand,
  updateBrand,
  deleteBrand,
  type Brand,
  type CreateBrandPayload,
  type UpdateBrandPayload,
} from '@/services/brands-api';

interface BrandFormState {
  name: string;
  aliasesText: string;       // newline- or comma-separated
  commenting_guidelines: string;
}

function aliasesFromText(text: string): string[] {
  return text
    .split(/[\n,]/g)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function aliasesToText(aliases: string[]): string {
  return aliases.join('\n');
}

export default function BrandsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Brand | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Brand | null>(null);

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: listBrands,
  });

  const createMut = useMutation({
    mutationFn: (payload: CreateBrandPayload) => createBrand(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brands'] });
      toast.success('Brand created');
      setCreating(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBrandPayload }) => updateBrand(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brands'] });
      toast.success('Brand updated');
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteBrand(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['brands'] });
      const orphaned = data.orphaned_activities + data.orphaned_sessions;
      toast.success(
        orphaned > 0
          ? `Brand deleted. ${data.orphaned_activities} activities and ${data.orphaned_sessions} sessions kept their history but lost brand attribution.`
          : 'Brand deleted',
      );
      setConfirmDelete(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sorted = useMemo(() => brands.slice().sort((a, b) => a.name.localeCompare(b.name)), [brands]);

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader>
            <SidebarTrigger>
              <PanelLeftOpen className="h-4 w-4" />
            </SidebarTrigger>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold leading-tight">Brands</h1>
              <p className="text-xs text-muted-foreground">
                Saved campaign profiles. Aliases drive substring matching; guidelines inject into the agent's system prompt.
              </p>
            </div>
            <div className="ml-auto">
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New brand
              </Button>
            </div>
        </PageHeader>

          <div className="flex-1 overflow-auto px-6 py-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading brands…
              </div>
            ) : sorted.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-20 text-center">
                <Tag className="h-8 w-8 text-muted-foreground/40" />
                <div className="text-sm font-medium">No brands yet</div>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Create one to start attributing comments to a campaign and injecting voice guidelines into your runs.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg divide-y">
                {sorted.map((brand) => (
                  <div key={brand.id} className="flex items-start justify-between gap-4 px-4 py-4 hover:bg-muted/30 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-3 mb-1.5">
                        <h2 className="text-sm font-semibold truncate">{brand.name}</h2>
                        <span className="text-xs font-mono text-muted-foreground">
                          {brand.aliases.length} alias{brand.aliases.length === 1 ? '' : 'es'}
                        </span>
                      </div>
                      {brand.aliases.length > 0 && (
                        <div className="text-xs text-muted-foreground/80 mb-1.5 truncate">
                          {brand.aliases.slice(0, 8).join(' · ')}
                          {brand.aliases.length > 8 && ` · +${brand.aliases.length - 8} more`}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {brand.commenting_guidelines || 'No commenting guidelines set.'}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(brand)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(brand)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      {creating && (
        <BrandFormDialog
          open
          title="Create brand"
          submitLabel="Create"
          isSubmitting={createMut.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(state) => createMut.mutate({
            name: state.name,
            aliases: aliasesFromText(state.aliasesText),
            commenting_guidelines: state.commenting_guidelines,
          })}
        />
      )}

      {editing && (
        <BrandFormDialog
          open
          title={`Edit ${editing.name}`}
          submitLabel="Save"
          initial={{
            name: editing.name,
            aliasesText: aliasesToText(editing.aliases),
            commenting_guidelines: editing.commenting_guidelines,
          }}
          isSubmitting={updateMut.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(state) => updateMut.mutate({
            id: editing.id,
            payload: {
              name: state.name,
              aliases: aliasesFromText(state.aliasesText),
              commenting_guidelines: state.commenting_guidelines,
            },
          })}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Activities and sessions previously attributed to this brand will keep their history
              but lose brand attribution (brand_id becomes NULL).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMut.isPending}
              onClick={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete brand'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function BrandFormDialog({
  open,
  title,
  submitLabel,
  initial,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  initial?: BrandFormState;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (state: BrandFormState) => void;
}) {
  const [state, setState] = useState<BrandFormState>(
    initial ?? { name: '', aliasesText: '', commenting_guidelines: '' },
  );

  const valid = state.name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The aliases drive case-insensitive substring matching against comment text.
            The commenting guidelines are injected into the agent's system prompt at run time.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="brand-name">Name</Label>
            <Input
              id="brand-name"
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              placeholder="Grove"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="brand-aliases">Aliases (one per line)</Label>
            <Textarea
              id="brand-aliases"
              value={state.aliasesText}
              onChange={(e) => setState({ ...state, aliasesText: e.target.value })}
              placeholder={'Grove\ngrove.tax\ngrove tax'}
              className="min-h-[100px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Each alias is matched case-insensitively as a substring of the comment text.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="brand-guidelines">Commenting guidelines</Label>
            <Textarea
              id="brand-guidelines"
              value={state.commenting_guidelines}
              onChange={(e) => setState({ ...state, commenting_guidelines: e.target.value })}
              placeholder={'When to recommend the brand, tone rules, hard-pass rules, example phrasings…'}
              className="min-h-[260px] font-mono text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(state)} disabled={!valid || isSubmitting}>
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
